import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useLastMessageName, usePreviewAvatarNode } from './hooks';
import {
  trackSwipeDeleteClick,
  trackSwipeMarkAsReadClick,
  trackSwipeMoveToArchiveClick,
  trackSwipeRemoveFromArchiveClick,
  trackSwipeUnblockClick,
} from './tracking';
import { openChatActionModal } from '../../actions/chatActionModal';
import { updateChatImages } from '../../actions/chatImages';
import { addChatToSelected, markChatsAsRead, removeChatFromSelected } from '../../actions/chatList';
import { moveToArchive, removeFromArchive, removeFromSpam } from '../../actions/chatOperations';
import { getMessages } from '../../actions/messages/getMessages';
import { openUserCard } from '../../actions/userCard/openUserCard';
import { ChatListItem } from '../../components/ChatListItem';
import { EChatListItemSwipeButtonType } from '../../components/ChatListItem/ChatListItemSwipeButton';
import { useCompanionData } from '../../hooks';
import { chatListSelector, hostTypeSelector, userIdSelector, currentOpenChatIDSelector } from '../../selectors';
import { EChatAction } from '../../types/chatActionModal';
import { TChatInListSchema } from '../../types/chatList';
import { EDeviceType } from '../../types/deviceType';
import { EHostType } from '../../types/hostType';
import { EMessageFileTypes } from '../../types/messageFiles';
import { EOutcomingStatus } from '../../types/messages';
import { TOffer } from '../../types/offer';
import { EChatRole } from '../../types/userData';

type TChatListItemContainerProps = {
  chat: TChatInListSchema;
};

export const ChatListItemContainer: React.VFC<TChatListItemContainerProps> = ({ chat }) => {
  const dispatch = useDispatch();

  const userId = useSelector(userIdSelector);
  const currentOpenChatId = useSelector(currentOpenChatIDSelector);
  const hostType = useSelector(hostTypeSelector);
  const device = useDeviceType();

  const { isChangeListActive, selectedChats, currentRoute } = useSelector(chatListSelector);

  const { message, data: { offer } = {}, chatId, users } = chat;
  const { type, files, userId: lastMessageUserId } = message;

  const companion = useCompanionData(chatId);
  const previewAvatarNode = usePreviewAvatarNode({ users });
  const lastMessageName = useLastMessageName({ users, lastMessageUserId });
  const opponent = users.find(user => user.chatRole === EChatRole.Opponent);

  const isUserLastMessage = message.userId === userId;

  const status = isUserLastMessage ? (message.read ? EOutcomingStatus.Read : EOutcomingStatus.Fulfilled) : undefined;

  const previewImage = React.useMemo(() => {
    if (offer && (offer as TOffer).photos) {
      return ((offer as TOffer).photos.find(p => p.isDefault) || offer.photos[0])?.miniUrl;
    }

    return companion?.avatarUrl;
  }, [offer, companion]);

  const isDesktopFrame = hostType === EHostType.Frame;
  const isMobile = device !== EDeviceType.Desktop;

  const onChatListItemClick = () => {
    if (isChangeListActive) {
      dispatch(
        selectedChats.includes(chat.chatId) ? removeChatFromSelected(chat.chatId) : addChatToSelected(chat.chatId),
      );
    } else {
      dispatch(getMessages(chat.chatId));
      dispatch(updateChatImages({ chatId: chat.chatId }));

      if (!isDesktopFrame && !isMobile) {
        dispatch(openUserCard(companion.companionId));
      }
    }
  };

  const onSwipeButtonClick = (type: EChatListItemSwipeButtonType) => {
    switch (type) {
      case EChatListItemSwipeButtonType.Archive: {
        dispatch(moveToArchive(chat.chatId));
        trackSwipeMoveToArchiveClick(offer, opponent?.userId);

        return;
      }
      case EChatListItemSwipeButtonType.Delete: {
        dispatch(addChatToSelected(chat.chatId));
        dispatch(
          openChatActionModal({
            buttonText: 'Удалить',
            text: 'Точно хотите удалить чат?',
            chatAction: EChatAction.Delete,
          }),
        );
        trackSwipeDeleteClick(offer, opponent?.userId);

        return;
      }
      case EChatListItemSwipeButtonType.Read: {
        dispatch(markChatsAsRead([chat.chatId]));
        trackSwipeMarkAsReadClick(offer, opponent?.userId);

        return;
      }
      case EChatListItemSwipeButtonType.Return: {
        dispatch(removeFromArchive(chat.chatId));
        trackSwipeRemoveFromArchiveClick(offer, opponent?.userId);

        return;
      }
      case EChatListItemSwipeButtonType.Unblock: {
        dispatch(removeFromSpam(chat.chatId));
        trackSwipeUnblockClick(offer, opponent?.userId);

        return;
      }
      case EChatListItemSwipeButtonType.Unread: {
        return;
      }
    }
  };

  const fileLastMessage = React.useMemo(() => {
    const isFileLastMessage = type === 'textAndFiles';

    if (isFileLastMessage) {
      const type = files?.fileTypes[0] as EMessageFileTypes;
      const name = files?.images?.[0]?.name || files?.documents?.[0]?.name || files?.imagesAsDocuments?.[0]?.name || '';

      return { type, name };
    }

    return undefined;
  }, [files?.documents, files?.fileTypes, files?.images, files?.imagesAsDocuments, type]);

  const isOfferLastMessage = React.useMemo(() => !!message.offers?.length, [message]);

  return (
    <ChatListItem
      chatId={chat.chatId}
      checkboxChecked={selectedChats.includes(chat.chatId)}
      checkboxShown={isChangeListActive}
      companion={companion}
      isActive={chat.chatId === currentOpenChatId}
      fileLastMessage={fileLastMessage}
      isOfferLastMessage={isOfferLastMessage}
      message={message.text}
      isMultilineMessage={!offer}
      onClick={onChatListItemClick}
      status={status}
      unreadCount={chat.unread}
      updated={chat.updatedMs}
      onSwipeButtonClick={onSwipeButtonClick}
      previewImage={previewImage}
      offerInfo={offer?.formattedFullInfo}
      currentRoute={currentRoute}
      previewAvatarNode={previewAvatarNode}
      lastMessageName={lastMessageName}
    />
  );
};
